import React from 'react';
import { useI18n } from '../context/I18nContext';
import { Link } from 'react-router-dom';
import { InboxIcon, KanbanIcon, TagIcon, PackageIcon, LogoIcon, PhoneIcon, SendIcon } from '../components/Icons';
import CookieConsent from '../components/CookieConsent';
import { useSettings } from '../context/SettingsContext';

const LandingPage = () => {
  const { settings } = useSettings();
  const features = [
    {
      icon: InboxIcon,
      title: t('landingpag.a1'),
      desc: t('landingpag.a2')
    },
    {
      icon: KanbanIcon,
      title: t('landingpag.a3'),
      desc: t('landingpag.a4')
    },
    {
      icon: TagIcon,
      title: t('landingpag.a5'),
      desc: t('landingpag.a6')
    },
    {
      icon: PackageIcon,
      title: t('landingpag.a7'),
      desc: t('landingpag.a8')
    }
  ];

  const { t } = useI18n();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="flex items-center justify-between px-8 md:px-20 h-[72px] bg-white border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
            <LogoIcon className="w-5 h-5 text-white" />
          </div>
          <span className="text-[22px] font-bold text-gray-900">{settings.companyName}</span>
        </div>
        <nav className="hidden md:flex items-center gap-8">
          <a href="#features" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">{t('land.features')}</a>
          <Link to="/sobre" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">{t('land.about')}</Link>
          <Link to="/contato" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">{t('land.contact')}</Link>
        </nav>
        <div className="flex items-center gap-4">
          <Link to="/login" className="hidden sm:block text-sm font-medium text-gray-900 hover:text-whatsapp transition-colors">
            Entrar
          </Link>
          <Link to="/login" className="px-6 py-3 bg-whatsapp text-white text-sm font-semibold rounded-lg hover:bg-whatsapp-dark transition-colors shadow-lg shadow-whatsapp/20">
            Acessar
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="flex flex-col items-center px-8 md:px-[120px] py-16 md:py-20 bg-gradient-to-b from-[#F8FAF9] to-white">
        <div className="flex items-center gap-2 px-4 py-2 bg-whatsapp/10 rounded-full mb-8 border border-whatsapp/20">
          <div className="w-2 h-2 bg-whatsapp rounded-full animate-pulse"></div>
          <span className="text-xs font-medium text-whatsapp">{t('land.officialApi')}</span>
        </div>
        <h1 className="text-4xl md:text-[52px] font-bold text-gray-900 text-center leading-tight mb-6">{t('landingpag.j1')}<br />{t('landingpag.j2')}</h1>
        <p className="text-lg text-gray-600 text-center mb-8 max-w-2xl">{t('landingpag.j3')}</p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Link to="/login" className="px-8 py-4 bg-whatsapp text-white text-base font-semibold rounded-xl hover:bg-whatsapp-dark transition-all shadow-xl shadow-whatsapp/30 hover:shadow-2xl hover:shadow-whatsapp/40 hover:-translate-y-0.5">{t('landingpag.j4')}</Link>
          <button className="px-8 py-4 bg-white text-gray-900 text-base font-medium rounded-xl hover:bg-gray-50 transition-colors border border-gray-200">{t('landingpag.j5')}</button>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="flex flex-col items-center px-8 md:px-[120px] py-16 md:py-20 bg-white">
        <span className="text-xs font-semibold text-whatsapp tracking-widest mb-4">{t('land.featuresUpper')}</span>
        <h2 className="text-3xl md:text-[40px] font-bold text-gray-900 text-center leading-tight mb-12">{t('landingpag.j6')}<br />{t('landingpag.j7')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 w-full max-w-6xl">
          {features.map((feature, i) => {
            const IconComponent = feature.icon;
            return (
              <div key={i} className="flex flex-col p-8 bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-100 hover:border-whatsapp/30 hover:shadow-xl hover:shadow-whatsapp/5 transition-all group">
                <div className="w-14 h-14 flex items-center justify-center bg-whatsapp/10 rounded-2xl mb-5 group-hover:bg-whatsapp/20 transition-colors">
                  <IconComponent className="w-7 h-7 text-whatsapp" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{feature.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA Section */}
      <section className="flex flex-col items-center px-8 md:px-[120px] py-16 md:py-20 bg-gray-900">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center leading-tight mb-4">{t('landingpag.j8')}<br />{t('landingpag.j9')}</h2>
        <p className="text-lg text-gray-400 text-center mb-8">{t('landingpag.j10')}</p>
        <Link to="/login" className="px-8 py-4 bg-whatsapp text-white text-base font-semibold rounded-xl hover:bg-whatsapp-dark transition-all shadow-xl shadow-whatsapp/30 hover:shadow-2xl hover:-translate-y-0.5">{t('landingpag.j11')}</Link>
      </section>

      {/* Footer */}
      <footer className="bg-gray-950 pt-16 pb-8">
        <div className="max-w-6xl mx-auto px-8 md:px-[120px]">
          {/* Footer Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                  <LogoIcon className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-white">{settings.companyName}</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mb-4 max-w-sm">
                {settings.description || t('landingpag.a9')}
              </p>
              <div className="space-y-2 text-sm text-gray-400">
                <p><strong className="text-gray-300">{settings.companyName}</strong></p>
                {settings.cnpj && <p>CNPJ: {settings.cnpj}</p>}
                {settings.address && <p>{settings.address}</p>}
                {(settings.city || settings.state || settings.zipCode) && (
                  <p>
                    {settings.city}{settings.state ? `/${settings.state}` : ''}
                    {settings.zipCode ? ` - CEP ${settings.zipCode}` : ''}
                  </p>
                )}
              </div>
            </div>

            {/* Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">{t('land.links')}</h4>
              <ul className="space-y-3 text-sm">
                <li>
                  <Link to="/sobre" className="text-gray-400 hover:text-white transition-colors">{t('landingpag.j12')}</Link>
                </li>
                <li>
                  <Link to="/contato" className="text-gray-400 hover:text-white transition-colors">{t('land.contact')}</Link>
                </li>
                <li>
                  <Link to="/privacidade" className="text-gray-400 hover:text-white transition-colors">{t('landingpag.j13')}</Link>
                </li>
                <li>
                  <Link to="/termos" className="text-gray-400 hover:text-white transition-colors">{t('landingpag.j14')}</Link>
                </li>
                <li>
                  <Link to="/exclusao-de-dados" className="text-gray-400 hover:text-white transition-colors">{t('landingpag.j15')}</Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-white font-semibold mb-4">{t('land.contact')}</h4>
              <ul className="space-y-3 text-sm">
                {settings.phone && (
                  <li className="flex items-center gap-2 text-gray-400">
                    <PhoneIcon className="w-4 h-4" />
                    <span>{settings.phone}</span>
                  </li>
                )}
                {settings.email && (
                  <li className="flex items-center gap-2 text-gray-400">
                    <SendIcon className="w-4 h-4" />
                    <span>{settings.email}</span>
                  </li>
                )}
              </ul>
              {settings.address && (
                <div className="mt-6">
                  <p className="text-xs text-gray-500 mb-2">{t('land.address')}</p>
                  <p className="text-sm text-gray-400">{settings.address}</p>
                  {settings.city && settings.state && (
                    <p className="text-sm text-gray-400">{settings.city} - {settings.state}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <span className="text-sm text-gray-500">
              © {new Date().getFullYear()} {settings.companyName}. Todos os direitos reservados.
              {settings.cnpj && <span className="block md:inline md:ml-2">CNPJ: {settings.cnpj}</span>}
            </span>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/privacidade" className="text-gray-500 hover:text-gray-300 transition-colors">
                Privacidade
              </Link>
              <Link to="/termos" className="text-gray-500 hover:text-gray-300 transition-colors">{t('landingpag.j16')}</Link>
              <Link to="/contato" className="text-gray-500 hover:text-gray-300 transition-colors">{t('landingpag.j17')}</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Cookie Consent Banner */}
      <CookieConsent />
    </div>
  );
};

export default LandingPage;
